import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Input, Button } from '@img/ui';
import { api, type Paginated } from '../lib/api';
import { SAUDI_CITIES } from '../lib/saudi-locations';
import { FavoriteButton } from '../components/FavoriteButton';
import { SEO } from '../components/SEO';
import { KEYWORDS_PROPERTIES } from '../lib/seo-keywords';
import { VerifiedBadge, type AdvertiserVerification } from '../components/VerifiedBadge';

interface PropertyListItem {
  id: string;
  type: string;
  purpose: string;
  title: string;
  price: number | string;
  area: number;
  rooms: number;
  bathrooms: number;
  city: string;
  district: string | null;
  coverImage: string | null;
  featured: boolean;
  reviewedAt?: string | null;
  adLicenseNumber?: string | null;
  // 🛡️ بيانات الموثوقية للمالك
  owner?: AdvertiserVerification & { id: string; name: string };
}

const PROPERTY_TYPES = [
  { value: '',             label: 'كل الأنواع' },
  { value: 'VILLA',        label: 'فيلا' },
  { value: 'APARTMENT',    label: 'شقة' },
  { value: 'LAND',         label: 'أرض' },
  { value: 'COMMERCIAL',   label: 'تجاري' },
  { value: 'OFFICE',       label: 'مكتب' },
];

const PURPOSES = [
  { value: '',     label: 'كل الأغراض' },
  { value: 'SALE',       label: 'للبيع' },
  { value: 'RENT',       label: 'للإيجار' },
  { value: 'INVESTMENT', label: 'للاستثمار' },
];

export function PropertiesPage() {
  const [filters, setFilters] = useState({ type: '', purpose: '', city: '', q: '', page: 1 });

  const { data, isLoading } = useQuery({
    queryKey: ['properties', filters],
    queryFn: () => api.get<Paginated<PropertyListItem>>('/api/properties', { query: filters }),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="عقارات للبيع والإيجار في السعودية 🏠 — العقار بصورة جديدة"
        description="العقار بصورة جديدة — تصفّح آلاف العقارات للبيع والإيجار والاستثمار في المملكة العربية السعودية: فلل، شقق، أراضي، عمائر، استراحات، مكاتب، محلات تجارية، مستودعات، مزارع. جميع الإعلانات مرخّصة من الهيئة العامة للعقار (REGA) والبورصة العقارية. شركة صورة العقارية — العقار بصورة جديدة."
        keywords={KEYWORDS_PROPERTIES}
        canonical="https://img.sa/properties"
        jsonLd={{
          '@type': 'CollectionPage',
          name: 'العقارات في السعودية — العقار بصورة جديدة',
          description: 'العقار بصورة جديدة — أكبر سوق عقاري سعودي يضم فلل وشقق وأراضي وعمائر للبيع والإيجار والاستثمار',
          url: 'https://img.sa/properties',
          about: {
            '@type': 'Service',
            name: 'سوق العقارات السعودي',
            serviceType: 'Real Estate Marketplace',
            provider: { '@type': 'Organization', name: 'شركة صورة العقارية', slogan: 'العقار بصورة جديدة' },
          },
        }}
      />
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark mb-1">العقارات المتاحة</h1>
          <p className="text-gray-600">تصفّح أحدث العروض المنشورة على المنصة</p>
        </div>
        <Link to="/submit-property">
          <Button variant="primary" size="lg">➕ أضف عقارك</Button>
        </Link>
      </div>

      {/* 🔍 Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select
              value={filters.purpose}
              onChange={(e) => setFilters({ ...filters, purpose: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل المدن</option>
              {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <Input
              placeholder="بحث عن…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
            />
          </div>
        </CardBody>
      </Card>

      {/* 🏘️ Grid */}
      {isLoading ? (
        <div className="text-center py-16 text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card>
          <CardBody className="text-center py-16">
            <div className="text-6xl mb-3">🏘️</div>
            <p className="text-gray-600">لا توجد عقارات تطابق البحث حالياً.</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.data.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <Button
                variant="outline" size="sm"
                disabled={filters.page <= 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                السابق
              </Button>
              <span className="px-4 py-2 text-sm font-bold text-ink">
                {filters.page} / {data.pagination.totalPages}
              </span>
              <Button
                variant="outline" size="sm"
                disabled={filters.page >= data.pagination.totalPages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                التالي
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PropertyCard({ property: p }: { property: PropertyListItem }) {
  // 📱 رابط واتساب لمشاركة العقار
  const propertyUrl = typeof window !== 'undefined' ? `${window.location.origin}/properties/${p.id}` : '';
  const shareWaUrl = `https://wa.me/?text=${encodeURIComponent(`${p.title}\n${propertyUrl}`)}`;

  return (
    <div className="relative group">
      <Link to={`/properties/${p.id}`}>
        <Card hover>
          <div className="h-48 bg-gradient-to-br from-navy to-navy-dark relative">
            {p.coverImage ? (
              <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-5xl">🏠</div>
            )}
            <div className="absolute top-3 right-3 flex gap-1 flex-wrap">
              {p.featured && <Badge color="gold">⭐ مميّز</Badge>}
              <Badge color={p.purpose === 'SALE' ? 'gold' : p.purpose === 'INVESTMENT' ? 'green' : 'navy'}>
                {p.purpose === 'SALE' ? 'بيع' : p.purpose === 'INVESTMENT' ? 'استثمار' : 'إيجار'}
              </Badge>
              {/* 🛡️ شارة موثوقية المعلن */}
              {p.owner && <VerifiedBadge user={p.owner} size="xs" />}
            </div>
            {/* ✓ شارة الاعتماد (لو فيه رقم رخصة) */}
            {p.adLicenseNumber && (
              <div className="absolute bottom-3 right-3">
                <span
                  title={`معتمد من شركة صورة العقارية · رقم الترخيص: ${p.adLicenseNumber}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold text-green-700 border border-green-200 shadow-sm"
                >
                  ✓ معتمد رسمياً
                </span>
              </div>
            )}
          </div>
        <CardBody>
          <h3 className="font-black text-ink text-base mb-1 line-clamp-1">{p.title}</h3>
          <p className="text-xs text-gray-500 mb-3">📍 {p.city}{p.district ? ` - ${p.district}` : ''}</p>
          <div className="flex items-center justify-between mb-3">
            <div className="text-navy-dark font-black text-lg">
              {Number(p.price).toLocaleString('en-US')} <span className="text-xs text-gray-500">ر.س</span>
            </div>
            <div className="flex gap-3 text-xs text-gray-600">
              <span>📐 {p.area} م²</span>
              <span>🛏 {p.rooms}</span>
              <span>🚿 {p.bathrooms}</span>
            </div>
          </div>
        </CardBody>
        </Card>
      </Link>

      {/* 💟 المفضلة (يمين أعلى) */}
      <div className="absolute top-3 left-3 z-10">
        <FavoriteButton propertyId={p.id} size="sm" />
      </div>

      {/* 📱 شريط التواصل السريع — يظهر دائماً في الأسفل */}
      <div className="absolute bottom-2 left-2 right-2 flex gap-1.5 z-10 pointer-events-none">
        <a
          href={shareWaUrl}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
          className="flex-1 pointer-events-auto flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-green-500/95 backdrop-blur hover:bg-green-600 text-white text-[11px] font-bold shadow-md transition"
          title="مشاركة عبر واتساب"
        >
          <span>📱</span><span>مشاركة</span>
        </a>
        <Link
          to={`/properties/${p.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 pointer-events-auto flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-navy-dark/95 backdrop-blur hover:bg-navy text-white text-[11px] font-bold shadow-md transition"
        >
          <span>👁️</span><span>التفاصيل</span>
        </Link>
      </div>
    </div>
  );
}
